/** @format */

import {
  configureStore,
  ThunkAction,
  Action,
  Middleware,
  combineReducers,
  isRejected,
  isPending,
} from "@reduxjs/toolkit";
import { api } from "./services/api";
import cookieReducer from "../features/cookie/cookieSlice";
import editableReducer from "../features/editable/editableSlice";
import featureFlagReducer from "../features/featureFlag/featureFlagSlice";
import printableReducer from "../features/printable/printableSlice";
import progressReducer from "../features/lessonProgress/processSlice";
import resourcesReducer from "../features/resources/resourcesSlice";
import sidebarReducer from "../features/sidebar/sidebarSlice";
import themeReducer from "../features/theme/themeSlice";
import authReducer from "../auth/authSlice";
import adminReducer from "../admin/adminSlice";
import contentEditorReducer from "../features/contentEditor/contentEditorSlice";
import deleteModalReducer from "../features/deleteModal/deleteModalSlice";
import cardReducer from "../features/activityCards/CardSlice";
import notificationsReducer, {
  setAddNotifications,
} from "../features/notifications/notificationsSlice";
import { setMaintenance } from "../admin/adminSlice";
import { LocaleText } from "../features/i18n/init";

// check if app is in maintenance
const maintenanceMiddleware: Middleware =
  (store) => (next) => async (action) => {
    if (
      !isPending(action) &&
      isRejected(action) &&
      action.meta.baseQueryMeta.response !== undefined &&
      action.meta.baseQueryMeta.response.status === 503
    )
      store.dispatch(
        setMaintenance({
          isMaintenance: true,
          message: action.payload.data as LocaleText,
        }),
      );
    else return next(action); // only continue if not in maintenance
  };

// Listen for mutation errors and create toast notification on failure
const mututationErrorMiddleware: Middleware = () => (next) => (action) => {
  if (action.type.includes("api/executeMutation/") && !isPending(action)) {
    const meta = action.meta;

    let method;

    switch (action.meta.baseQueryMeta.request.method) {
      case "DELETE":
        method = "delete";
        break;
      case "POST":
        method = "save";
        break;
    }

    const endpointNameToReadable = meta.arg.endpointName
      .split(/(?<![A-Z])(?=[A-Z])/)
      .join(" ")
      .toLocaleLowerCase();
    if (isRejected(action)) {
      store.dispatch(
        setAddNotifications({
          date: meta?.fulfilledTimeStamp,
          id: meta.requestId,
          title: `Error on ${method}`,
          msg: `Failed to ${endpointNameToReadable}`,
          type: "danger",
        }),
      );
    }
  }

  return next(action);
};

const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  cookie: cookieReducer,
  editable: editableReducer,
  featureFlag: featureFlagReducer,
  notifications: notificationsReducer,
  printable: printableReducer,
  progress: progressReducer,
  resources: resourcesReducer,
  sidebar: sidebarReducer,
  theme: themeReducer,
  auth: authReducer,
  admin: adminReducer,
  contentEditor: contentEditorReducer,
  deleteModal: deleteModalReducer,
  cards: cardReducer,
});

export function setupStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }).concat(
        api.middleware,
        maintenanceMiddleware,
        mututationErrorMiddleware,
      ),
    devTools: true,
    preloadedState: preloadedState,
  });

  return store;
}

export const store = setupStore();

export type AppDispatch = AppStore["dispatch"];
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
